import React, { Fragment, useContext } from 'react';
import { AgGridReact } from 'ag-grid-react';
import CloseIcon from '@material-ui/icons/Close';
import { Box, IconButton } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import './style.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import previewTableStyles from './PreviewTable.style';
import { PreviewElementContext } from '../Preview.context';
import { FILTER_LOWER } from 'constants/constant';
import { formatCustomAttrColId } from 'helper/utils';

const AGGridTable = React.memo((props) => {
	const { data, columnDefs, onColumnMoved, style } = props;

	const sizeColumnsToFit = (event) =>
		event.api.sizeColumnsToFit({
			defaultMinWidth: 350
		});

	const onGridReady = (event) => {
		if (props.onReadyFn) {
			props.onReadyFn(event);
		}
		sizeColumnsToFit(event);
	};
	const onGridColumnsChanged = (event) => sizeColumnsToFit(event);

	const otherProps = {
		rowHeight: 40,
		headerHeight: 38,
		groupHeaderHeight: 0,
		defaultColDef: {
			lockPinned: true
		},
		tooltipShowDelay: 2000
	};

	return (
		<div className="ag-theme-alpine" style={style}>
			<AgGridReact
				rowData={data}
				{...otherProps}
				suppressNoRowsOverlay={true}
				columnDefs={columnDefs}
				onGridReady={onGridReady}
				onColumnMoved={onColumnMoved}
				suppressRowHoverHighlight={true}
				onGridColumnsChanged={onGridColumnsChanged}
				suppressDragLeaveHidesColumns={true}
			/>
		</div>
	);
});

AGGridTable.displayName = 'AGGridTable';

export const CustomHeaderComponent = (headerProps) => {
	const classes = previewTableStyles();
	// eslint-disable-next-line prettier/prettier
	const {
		displayName,
		column,
		deleteColumn,
		itemData,
		isFilterColAllowed,
		isDeleteColAllowed,
		isRightPanelOpened,
		drawerWidth
	} = headerProps;
	const { cbSetShowPreviewElement, cbUsrSelection } = useContext(PreviewElementContext);

	const onDeleteClick = () => deleteColumn(displayName, column?.colId);

	const onFilterClick = () => {
		cbSetShowPreviewElement(FILTER_LOWER, true);
		cbUsrSelection([itemData]);
	};

	return (
		<Fragment>
			<Box
				className={classes.scrollHeader}
				style={{
					width: isRightPanelOpened ? `calc(100vw - ${drawerWidth}px)` : '100%',
					alignItems: 'center',
					paddingBottom: '10px',
					overflow: 'hidden'
				}}>
				{displayName}
			</Box>

			<Box className="column-header-icons">
				{isFilterColAllowed && (
					<IconButton
						id={`${displayName}-filter`}
						className={classes.iconButton}
						aria-label={`filter ${displayName}`}
						style={{ overflow: 'hidden' }}
						onClick={onFilterClick}>
						<FilterListIcon
							fontSize="small"
							className={`${classes.showIcon} ${classes.highlightFilter}`}
						/>
					</IconButton>
				)}
				{isDeleteColAllowed && (
					<IconButton
						id={`${displayName}-delete`}
						className={classes.iconButton}
						aria-label={`delete ${displayName}`}
						onClick={onDeleteClick}>
						<CloseIcon fontSize="small" className={classes.showIcon} />
					</IconButton>
				)}
			</Box>
		</Fragment>
	);
};

CustomHeaderComponent.displayName = 'CustomHeaderComponent';

const formatCustomField = (field) => {
	//custom attr use it's table name as identifier
	return field?.split('|')?.[0];
};

const formatField = (field, isBasic) => {
	/**
	 * Getting only the part after the "." which is easier to compare with the column field names
	 */
	if (isBasic) {
		return field;
	}
	return field?.split('|')?.[1] ? field.split('|')?.[1] : field;
};

export const getColumnDefs = (
	deleteColumn,
	headers = [],
	isRightPanelOpened,
	drawerWidth,
	isBasic,
	isTimeAcross,
	pivotStatus = false
) => {
	const dimensions = [];
	const metrics = [];
	const times = [];
	const customAttrs = [];
	const placeholders = [];
	headers.forEach((header) => {
		const otherProps = {
			deleteColumn,
			isFilterColAllowed: header.isFilterColAllowed,
			isDeleteColAllowed: header.isDeleteColAllowed,
			itemData: {
				parent: header.item.parent || '',
				...header?.item
			},
			isRightPanelOpened,
			drawerWidth
		};
		if (header.item.parent === 'placeholder') {
			placeholders.push({
				colId: header?.field,
				field: header?.field,
				headerName: header?.headerName,
				headerTooltip: header?.headerName,
				// supressMovable true prevents users from dragging the columns
				suppressMovable: pivotStatus,
				/* eslint-disable react/display-name */
				headerComponent: (props) => <CustomHeaderComponent {...props} {...otherProps} />
			});
		} else if (header.item.parent === 'dimensions') {
			dimensions.push({
				colId: header?.item?.technicalName,
				field: formatField(header?.field, isBasic),
				headerName: header?.headerName,
				headerTooltip: header?.headerName,
				// supressMovable true prevents users from dragging the columns
				suppressMovable: pivotStatus,
				/* eslint-disable react/display-name */
				headerComponent: (props) => <CustomHeaderComponent {...props} {...otherProps} />
			});
		} else if (header.item.parent === 'measures') {
			metrics.push({
				colId: header?.item?.technicalName,
				field: formatField(header?.field, isBasic),
				headerName: header?.headerName,
				headerTooltip: header?.headerName,
				// supressMovable true prevents users from dragging the columns
				suppressMovable: pivotStatus,
				/* eslint-disable react/display-name */
				headerComponent: (props) => <CustomHeaderComponent {...props} {...otherProps} />
			});
		} else if (header.item.parent === 'customAttribute') {
			customAttrs.push({
				colId: formatCustomAttrColId(header?.item?.businessName),
				field: formatCustomField(header?.field),
				headerName: header?.headerName,
				headerTooltip: header?.headerName,
				suppressMovable: true,
				/* eslint-disable react/display-name */
				headerComponent: (props) => <CustomHeaderComponent {...props} {...otherProps} />
			});
		} else {
			times.push({
				colId: header?.item?.technicalName,
				field: formatField(header?.field, isBasic),
				headerName: header?.headerName,
				headerTooltip: header?.headerName,
				suppressMovable: pivotStatus,
				/* eslint-disable react/display-name */
				headerComponent: (props) => <CustomHeaderComponent {...props} {...otherProps} />
			});
		}
	});

	if (isTimeAcross) {
		return [
			{
				headerName: '',
				pinned: true,
				maxWidth: 52,
				lockPosition: 'left',
				valueGetter: 'node.rowIndex + 1',
				cellStyle: { fontSize: '14px', paddingLeft: '16px', paddingRight: '16px' }
			},
			{
				headerName: '',
				marryChildren: true,
				children: [...dimensions]
			},
			{
				headerName: '',
				marryChildren: true,
				children: [...customAttrs]
			},
			{
				headerName: '',
				marryChildren: true,
				children: [...placeholders]
			},
			{
				headerName: '',
				marryChildren: true,
				children: [...times]
			}
		];
	}

	return [
		{
			headerName: '',
			pinned: true,
			maxWidth: 52,
			lockPosition: 'left',
			valueGetter: 'node.rowIndex + 1',
			cellStyle: { fontSize: '14px', paddingLeft: '16px', paddingRight: '16px' }
		},
		{
			headerName: '',
			marryChildren: true,
			children: [...times]
		},
		{
			headerName: '',
			marryChildren: true,
			children: [...dimensions]
		},
		{
			headerName: '',
			marryChildren: true,
			children: [...metrics]
		},
		{
			headerName: '',
			marryChildren: true,
			children: [...customAttrs]
		}
	];
};

export default AGGridTable;
